import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import SalonThemeApplyScope, {
  isSalonThemeApplyScopeEligible,
} from './SalonThemeApplyScope';

const SalonThemeActionBar = ({
  dirty,
  disabled,
  mobile,
  accentColor,
  scopeProps,
  onCancelChanges,
  onSave,
}) => {
  const { t } = useTranslation();
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const scopeDialogOpenerRef = useRef(null);
  const scopeAvailable = isSalonThemeApplyScopeEligible(scopeProps);
  const scopeDialogTitleId = 'salon-theme-apply-scope-dialog-title';

  useEffect(() => {
    if (mobile && scopeAvailable && scopeProps?.error) {
      setScopeDialogOpen(true);
    }
  }, [mobile, scopeAvailable, scopeProps?.error]);

  const closeScopeDialog = () => {
    setScopeDialogOpen(false);
    scopeDialogOpenerRef.current?.focus();
  };

  return (
    <Box
      component="footer"
      data-testid="salon-theme-action-bar"
      style={mobile ? { position: 'sticky', bottom: 0 } : undefined}
      sx={{
        zIndex: mobile ? 1 : 'auto',
        bgcolor: mobile ? 'background.paper' : 'transparent',
        borderTop: mobile ? 1 : 0,
        borderColor: 'divider',
        py: mobile ? 1.5 : 0,
      }}
    >
      {dirty && (
        <Typography role="status" color="text.secondary" variant="body2">
          {t('salon_theme.preview.draft_updated')}
        </Typography>
      )}

      <Stack
        direction={mobile ? 'row' : { xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={mobile ? 'center' : { md: 'center' }}
        sx={{ mt: dirty ? 1 : 0 }}
      >
        {!mobile && scopeAvailable && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SalonThemeApplyScope
              {...scopeProps}
              disabled={disabled}
              accentColor={accentColor}
            />
          </Box>
        )}

        {mobile && scopeAvailable && (
          <Button
            ref={scopeDialogOpenerRef}
            type="button"
            variant="text"
            disabled={disabled}
            onClick={() => setScopeDialogOpen(true)}
            sx={{ minHeight: 44, minWidth: 0 }}
          >
            {t('salon_theme.apply_scope.open')}
          </Button>
        )}

        <Box sx={{ flex: mobile ? 1 : '0 0 auto' }} />
        <Button
          type="button"
          variant="text"
          onClick={onCancelChanges}
          disabled={!dirty || disabled}
          sx={{ minHeight: 44 }}
        >
          {t('salon_theme.actions.cancel_changes')}
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={onSave}
          disabled={!dirty || disabled}
          style={{ backgroundColor: accentColor }}
          sx={{ minHeight: 44 }}
        >
          {t('salon_theme.actions.save')}
        </Button>
      </Stack>

      {mobile && scopeAvailable && (
        <Dialog
          open={scopeDialogOpen}
          onClose={closeScopeDialog}
          aria-labelledby={scopeDialogTitleId}
        >
          <DialogTitle id={scopeDialogTitleId}>
            {t('salon_theme.apply_scope.legend')}
          </DialogTitle>
          <DialogContent>
            <SalonThemeApplyScope
              {...scopeProps}
              disabled={disabled}
              accentColor={accentColor}
            />
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={closeScopeDialog}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SalonThemeActionBar;
